import {Injectable} from '@angular/core';
import {formatDate} from '@angular/common';
import {map, Observable} from 'rxjs';
import {ApiService} from './api/api.service';
import {Registration, RegistrationV2} from '../classes/registration';

@Injectable({
    providedIn: 'root'
})
export class RegistrationService {
    constructor(private apiService: ApiService) {
    }

    public getAll(selectedDate: Date) {
        return this.apiService.getCallData$<{ comments: Record<string, string>, registrations: Registration[] }>('registrations/all', {date: selectedDate});
    }

    public save(
        selectedDate: Date,
        registrations: Registration[],
        comments: Record<string, string>
    ) {
        return this.apiService.postCall$<Registration[]>('registrations/save', {
            date: selectedDate,
            registrations: registrations.map((r) => ({
                ...r,
                from: formatDate(r.from, 'yyyy-MM-dd HH:mm', 'nl'),
                till: formatDate(r.till, 'yyyy-MM-dd HH:mm', 'nl')
            })),
            comments
        });
    }

    public getWeeklyHours(): Observable<any> {
        return this.apiService.getCallData$<any>('registrations/weeklyhours', {});
    }

    public changeProject(registrationId: number, workProjectId: number): Observable<RegistrationV2> {
        return this.apiService.postCall$<RegistrationV2>('registrations/change-project', {
            registrationId,
            workProjectId
        }).pipe(
            map(response => response.data)
        );
    }

    approveRegistration(registrationId: number) {
        return this.apiService.postCall$<Registration>(`registrations/approve/${registrationId}`);
    }

    disapproveRegistration(registrationId: number) {
        return this.apiService.postCall$<Registration>(`registrations/disapprove/${registrationId}`);
    }

    getDetails(year: number, period?: number, userId?: number, companyId?: number) {
        return this.apiService.getCallData$<{
            period: { iso_period_year: number, period: number, start: string, end: string },
            registrations: RegistrationV2[],
            comments: { id: number, user_id: number, description: string, datetime: string }[]
        }>(`registrations`, {
            ...(this.apiService.buildParams({year, period, userId, companyId}))
        });
    }

    getDetailsByWeek(year: number, week?: number, userId?: number, companyId?: number) {
        return this.apiService.getCallData$<{
            period: { iso_period_year: number, period: number, start: string, end: string },
            registrations: RegistrationV2[],
            comments: { id: number, user_id: number, description: string, datetime: string }[]
        }>(`registrations`, {
            ...(this.apiService.buildParams({year, week, userId, companyId}))
        });
    }

    getTotals(year?: number, period?: number, userId?: number, companyId?: number, groupBy: string | string[] = 'work_project_id') {
        return this.apiService.getCallData$<RegistrationTotal[]>(`registrations/totals`, {
            ...(this.apiService.buildParams({year, period, userId, companyId, groupBy: Array.isArray(groupBy) ? groupBy.join(',') : groupBy}))
        });
    }

    getProfileTotals(userId?: number) {
        return this.apiService.getCallData$<ProfileTotals>(`registrations/profile-totals`, {
            ...(this.apiService.buildParams({userId}))
        });
    }

    getProfileTotalsOverview() {
        return this.apiService.getCallData$<ProfileTotals[]>(`registrations/profile-totals/overview`, {});
    }
}

export interface ProfileTotals {
    user_id: number,
    name: string,
    employment_start: string | null,
    until: string,
    total_minutes: number,
    worked_minutes: number,
    vacation_minutes: number,
    vacation_balance_minutes: number,
    balance_minutes: number,
    per_year: ProfileTotalsYear[]
}

export interface ProfileTotalsYear {
    year: number,
    registered_minutes: number,
    vacation_entitlement_minutes: number,
    vacation_minutes: number,
    vacation_planned_minutes: number,
    sick_minutes: number,
    sick_percentage: number | null,
    balance_minutes: number
}

export interface RegistrationTotal {
    time: number,
    totaltime: number,
    user_id?: null | number,
    company_id?: null | number,
    work_project_id?: null | number
}
